'use strict';

var jsxRuntime = require('react/jsx-runtime');
var tailwindMerge = require('tailwind-merge');
var Typography = require('../../Base/Typography/Typography.js');
var Box = require('../../Base/Layouts/Box/Box.js');
require('../../Base/Layouts/FullBleed/FullBleed.js');

/**
 * 여러 옵션 중 하나를 선택할 수 있는 세그먼트 컨트롤 컴포넌트입니다.
 *
 * @param {Object} props
 * @param {'sm' | 'md'} [props.size='md'] - 세그먼트 컨트롤 크기
 * @param {Array<{key: string, name: string}>} props.valueList - 선택 가능한 옵션들의 배열 ({key, name}[])
 * @param {string} props.selectedValue - 현재 선택된 옵션의 key 값
 * @param {string} [props.name='segmented-control'] - 라디오 그룹의 name 속성
 * @param {(value: string) => void} props.onChange - 선택 값이 변경될 때 호출되는 콜백 함수
 *
 * @example
 * ```tsx
 * const options = [
 *   { key: 'daily', name: '일간' },
 *   { key: 'weekly', name: '주간' },
 *   { key: 'monthly', name: '월간' }
 * ];
 *
 * const [selected, setSelected] = useState('daily');
 *
 * return (
 *   <SegmentedControl
 *     valueList={options}
 *     selectedValue={selected}
 *     onChange={setSelected}
 *   />
 * );
 * ```
 */ const SegmentedControl = ({ valueList, selectedValue, size = 'md', name = 'segmented-control', onChange })=>{
    const commonSelectedStyle = 'border border-primary bg-primary-10 text-primary';
    const commonUnselectedStyle = 'bg-w-gray-50 text-w-gray-300';
    const sizeStyles = {
        sm: {
            selected: 'px-[15px] py-[7px]',
            unselected: 'px-4 py-2'
        },
        md: {
            selected: 'px-[15px] py-[11px]',
            unselected: 'px-4 py-3'
        }
    };
    const firstStyle = 'rounded-l-xl';
    const lastStyle = 'rounded-r-xl';
    return jsxRuntime.jsx(Box, {
        className: "flex w-full overflow-hidden rounded-xl",
        role: "radiogroup",
        "aria-label": "segmented control",
        "data-testid": "design-system--segmented-control",
        children: valueList.map((value, idx)=>{
            const isFirst = idx === 0;
            const isLast = idx === valueList.length - 1;
            const isSelected = selectedValue === value.key;
            const needFontMedium = isSelected && size === 'sm';
            return jsxRuntime.jsxs("label", {
                className: tailwindMerge.twJoin('relative cursor-pointer select-none text-center', isSelected ? commonSelectedStyle : commonUnselectedStyle, isSelected ? sizeStyles[size].selected : sizeStyles[size].unselected, isFirst && firstStyle, isLast && lastStyle),
                "data-testid": "design-system--segmented-control-item",
                children: [
                    jsxRuntime.jsx("input", {
                        type: "radio",
                        name: name,
                        value: value.key,
                        checked: isSelected,
                        "aria-checked": isSelected,
                        onChange: (e)=>onChange(e.target.value),
                        className: "absolute h-0 w-0 opacity-0"
                    }),
                    jsxRuntime.jsx(Typography, {
                        variant: size === 'md' ? 'body1' : 'body2',
                        className: tailwindMerge.twJoin(needFontMedium ? 'font-medium' : 'font-normal'),
                        children: value.name
                    })
                ]
            }, value.key);
        })
    });
};

module.exports = SegmentedControl;
